import { useState, useEffect, useCallback } from "react";
import { Fuel, Plus, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, fmtDate, todayIST } from "@/lib/format";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const PHYSIO_CATS = ["petrol", "travel", "other"];
const ADMIN_CATS = ["petrol", "salary", "rent", "electricity", "equipment", "maintenance", "marketing", "software", "medical_supplies", "office_supplies", "other"];

export default function Expenses() {
  const { user } = useAuth();
  const isPhysio = user?.role === "physiotherapist";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = status !== "all" ? { status } : {};
      const { data } = await api.get("/expenses", { params });
      setItems(data.items);
    } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => { try { await api.post(`/expenses/${id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(apiError(e)); } };
  const reject = async (id) => { if (!reason.trim()) { toast.error("Reason required"); return; } try { await api.post(`/expenses/${id}/reject`, { reason }); toast.success("Rejected"); setReason(""); load(); } catch (e) { toast.error(apiError(e)); } };

  const total = items.filter((e) => e.status === "approved").reduce((s, e) => s + e.amount, 0);

  return (
    <div>
      <PageHeader title="Expenses" subtitle={`Approved total ${inr(total)}`} testid="expenses-page">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36" data-testid="exp-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
        </Select>
        <Button onClick={() => setOpen(true)} data-testid="add-expense-btn" className="bg-bk-green hover:bg-bk-greenDark"><Plus className="h-4 w-4 mr-1" /> Add Expense</Button>
      </PageHeader>

      {loading ? <Loading /> : items.length === 0 ? (
        <EmptyState icon={Fuel} title="No expenses" />
      ) : (
        <div className="grid gap-2.5 lg:grid-cols-2">
          {items.map((e) => (
            <Card key={e.id} data-testid={`expense-${e.expense_id}`} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">{e.category?.replace(/_/g, " ")}</Badge>
                    <StatusBadge status={e.status} />
                  </div>
                  <p className="font-display font-bold text-lg text-slate-800 tabular-nums mt-1">{inr(e.amount)}</p>
                  <p className="text-xs text-slate-500">{e.description} {e.patient_route && `· ${e.patient_route}`}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{fmtDate(e.date)} · {e.paid_by} {e.physio_name && `· ${e.physio_name}`}</p>
                </div>
              </div>
              {user?.role === "admin" && e.status === "pending" && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => approve(e.id)} data-testid={`approve-exp-${e.expense_id}`} className="flex-1 h-8 bg-bk-green hover:bg-bk-greenDark"><CheckCircle2 className="h-4 w-4 mr-1" />Approve</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="flex-1 h-8 text-bk-danger"><XCircle className="h-4 w-4 mr-1" />Reject</Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Reject expense?</AlertDialogTitle><AlertDialogDescription>Provide a reason.</AlertDialogDescription></AlertDialogHeader>
                      <div className="space-y-1.5"><Label>Reason *</Label><Input value={reason} onChange={(ev) => setReason(ev.target.value)} /></div>
                      <AlertDialogFooter><AlertDialogCancel onClick={() => setReason("")}>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => reject(e.id)} className="bg-bk-danger hover:bg-red-700">Reject</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <ExpenseDialog open={open} onOpenChange={setOpen} cats={isPhysio ? PHYSIO_CATS : ADMIN_CATS} onDone={load} />
    </div>
  );
}

function ExpenseDialog({ open, onOpenChange, cats, onDone }) {
  const [category, setCategory] = useState(cats[0]);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayIST());
  const [method, setMethod] = useState("cash");
  const [description, setDescription] = useState("");
  const [route, setRoute] = useState("");
  const [distance, setDistance] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) { setCategory(cats[0]); setAmount(""); setDate(todayIST()); setDescription(""); setRoute(""); setDistance(""); } }, [open, cats]);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter amount"); return; }
    setLoading(true);
    try {
      await api.post("/expenses", { category, amount: Number(amount), date, method, description, patient_route: route, distance: distance ? Number(distance) : null });
      toast.success("Expense added");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Add Expense</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Category</Label>
              <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="exp-category"><SelectValue /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="exp-amount" /></div>
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Method</Label>
              <Select value={method} onValueChange={setMethod}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank">Bank</SelectItem><SelectItem value="card">Card</SelectItem></SelectContent></Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Route / Patient</Label><Input value={route} onChange={(e) => setRoute(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Distance (km)</Label><Input type="number" value={distance} onChange={(e) => setDistance(e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="exp-save" className="bg-bk-green hover:bg-bk-greenDark">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
